import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IKeytipDataProps } from "@fluentui/react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { useKeytipRef } from "@fluentui/react";
import { useMergedRefs } from "@fluentui/react-hooks";
import type { RefObjectFunction } from "@fluentui/react-hooks";

import type { OnExecuteParameter, UILocation } from "@1js/acui-common";

import { defaultKeytipExecute } from "./keytipExecution";
import { useOnExecuteFromKeytip } from "./useOnExecuteFromKeytip";
import { useV9KeytipEnabled } from "./hooks/useV9KeytipEnabled";
import { useAppComponentKeytip as useAppComponentKeytipV9 } from "./Next";
import type { AppKeytipProps } from "./AppKeytip.types";

export type AppComponentKeytipProps = {
  checked?: boolean;
  id: string;
  location?: UILocation;
  onExecute?: <TOnExecuteParameter extends OnExecuteParameter>(
    parameter: TOnExecuteParameter
  ) => void;
  shouldTakeFocus?: boolean;
  toggled?: boolean;
};

export type AppComponentKeytipOptions = {
  directClickRef?: React.RefObject<HTMLElement | null | undefined>;
  keytipProps?: AppKeytipProps;
  useDirectClick?: boolean;
};

export type AppComponentWithKeytip =
  | "AppButton"
  | "AppCheckbox"
  | "AppCombobox"
  | "AppInput"
  | "AppMenuItem"
  | "AppPromptInput"
  | "AppSuggestion"
  | "AppTextarea"
  | "AppTellMeControl";

export type DeriveKeytipFunction = (
  keytipProps?: AppKeytipProps,
  onExecute?: (executeTarget: HTMLElement, target?: HTMLElement) => void
) => AppKeytipProps;

export const useAppComponentKeytip: <TProps extends AppComponentKeytipProps>(
  props: TProps,
  ref: RefObjectFunction<HTMLElement | null | undefined>,
  componentType: AppComponentWithKeytip,
  deriveKeytip: DeriveKeytipFunction,
  options?: AppComponentKeytipOptions
) => RefObjectFunction<HTMLElement | null | undefined> = (
  props,
  ref,
  type,
  deriveKeytip,
  options
) => {
  const v9KeytipEnabled = useV9KeytipEnabled();

  const mergedRefV9 = useAppComponentKeytipV9(
    props,
    ref,
    type,
    deriveKeytip as any,
    options as AppComponentKeytipOptions
  );
  const { keytipProps } = options || {};
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const executeFromKeytip = useOnExecuteFromKeytip(props, ref, type, options);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const keytipExecute = React.useCallback(
    (executeTarget: HTMLElement) => {
      defaultKeytipExecute(executeTarget, executeFromKeytip);
    },
    [executeFromKeytip]
  );
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const keytipRefData = React.useMemo(() => {
    return { keytipProps: deriveKeytip(keytipProps, keytipExecute) };
  }, [deriveKeytip, keytipProps, keytipExecute]);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const localRef = useKeytipRef(keytipRefData as IKeytipDataProps);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const mergedRef = useMergedRefs<HTMLElement | null | undefined>(
    ref,
    localRef
  );

  return v9KeytipEnabled ? mergedRefV9 : mergedRef;
};
